import { DOCUMENT } from "@angular/common";
import { afterNextRender, Component, computed, DestroyRef, effect, ElementRef, inject, input, linkedSignal, output, viewChild } from "@angular/core";
import { AuthService } from "@kno2/core/auth";
import { APP_CONFIG_TOKEN } from "@kno2/core/config";
import { PageEvent, PageRotationEvent, PdfViewerComponent } from "@kno2/ngx-pdf-viewer";
import { LoadingSpinnerComponent } from "../loading-spinner";
import { MessageAttachment, PdfViewerOptions } from "./pdf-viewer.models";

export const NOT_SELECTED = "/documents/no-document-selected.pdf";
export const FAILED = "/documents/preview-not-available.pdf";
export const PENDING = "/documents/document-being-converted.pdf";

export function isStructuredDocument(attachment: MessageAttachment): boolean {
    if (!attachment?.fileName) return false;
    const fileName = attachment.fileName;
    const ext = fileName.substring(fileName.lastIndexOf(".") + 1).toLowerCase();
    return ext === "hl7" || ext === "ccd" || ext === "cda" || ext === "xml";
}

@Component({
    selector: "kno2-pdf-viewer-container",
    templateUrl: "./pdf-viewer-container.component.html",
    imports: [PdfViewerComponent, LoadingSpinnerComponent]
})
export class PdfViewerContainerComponent {
    private document = inject(DOCUMENT);
    private appConfig = inject(APP_CONFIG_TOKEN);
    private authService = inject(AuthService);
    private elementRef = inject(ElementRef);
    private destroyRef = inject(DestroyRef);

    private readonly defaultUrlFn = computed(
        () => (attachment: MessageAttachment) => `${this.appConfig.baseApiUrl}/api/messages/${attachment.messageId}/attachments/${attachment.id}`
    );

    public pdfViewer = viewChild.required(PdfViewerComponent);

    public attachment = input<MessageAttachment | null>(null);
    public options = input<PdfViewerOptions>({});
    public disableAudit = input<boolean>(false);
    public urlFn = input<((attachment: MessageAttachment) => string) | null>(null);

    public change = output<MessageAttachment>();
    public attachmentRead = output<{ messageId: string; attachmentId: string }>();

    public isLoading = linkedSignal(() => (this.attachment(), true));

    public pdfSrc = computed(() => this.getSrc(this.attachment()));
    public effectiveOptions = computed(() => {
        const opts = this.options();
        const att = this.attachment();
        if (att && isStructuredDocument(att)) {
            return {
                ...opts,
                rotate: "hidden" as const,
                remove: "hidden" as const
            };
        }
        return opts;
    });

    public constructor() {
        effect(() => {
            const att = this.attachment();
            if (!this.disableAudit() && att?.messageId) {
                this.attachmentRead.emit({ messageId: att.messageId, attachmentId: att.id });
            }
        });

        afterNextRender(() => {
            const printContainer = this.elementRef.nativeElement.querySelector("#printContainer");
            if (printContainer) {
                this.document.body.appendChild(printContainer);
                this.destroyRef.onDestroy(() => {
                    if (printContainer.parentNode) {
                        printContainer.remove();
                    }
                });
            }
        });
    }

    private getTransforms(attachment: MessageAttachment): { rotations?: { [key: number]: number }; deletions?: number[] } | null {
        if (!attachment) return null;
        return (attachment.$$transforms = attachment.$$transforms || (attachment.transforms && { ...attachment.transforms }) || {});
    }

    private getSrc(attachment: MessageAttachment | null): string | { url: string; httpHeaders: Record<string, string>; cached?: boolean } {
        if (attachment) {
            switch (attachment.previewAvailable) {
                case "Available": {
                    const urlFn = this.urlFn() || this.defaultUrlFn();
                    const url = urlFn(attachment);

                    const isRestored = attachment.$$restored === true;
                    if (isRestored) {
                        attachment.$$restored = undefined;
                    }

                    const result: { url: string; httpHeaders: Record<string, string>; cached?: boolean } = {
                        url: url,
                        httpHeaders: {
                            Accept: "application/pdf",
                            Authorization: `Bearer ${this.authService.accessToken}`
                        }
                    };

                    if (isRestored) {
                        result.cached = false;
                    }

                    return result;
                }

                case "Pending":
                    return PENDING;

                case "Failed":
                    return FAILED;
            }
        }

        return NOT_SELECTED;
    }

    public onPageRotate(event: PageRotationEvent): void {
        const att = this.attachment();
        if (event && att) {
            const transforms = this.getTransforms(att);
            if (transforms) {
                const rotations = (transforms.rotations = transforms.rotations || {});
                const currentRotation = rotations[event.pageNumber - 1] || 0;
                const baseRotation = att.transforms?.rotations?.[event.pageNumber - 1] || 0;
                rotations[event.pageNumber - 1] = (((currentRotation - baseRotation + event.rotation) % 360) + 360) % 360;
                this.change.emit(att);
            }
        }
    }

    public onPageRemove(event: PageEvent): void {
        const att = this.attachment();
        if (event && att) {
            const transforms = this.getTransforms(att);
            if (transforms) {
                const deletions = (transforms.deletions = transforms.deletions || []);
                deletions.push(event.pageNumber - 1);
                this.change.emit(att);
            }
        }
    }

    public onDocumentReload(): void {
        const att = this.attachment();
        if (att) {
            const transforms = this.getTransforms(att);
            if (transforms) {
                delete transforms.rotations;
                delete transforms.deletions;
                this.change.emit(att);
            }
        }
    }

    public onLoad(): void {
        this.isLoading.set(false);
    }

    public onPagesInit(): void {
        this.isLoading.set(false);
    }

    public onPagesLoaded(): void {
        const att = this.attachment();
        if (att?.$$transforms) {
            delete att.$$transforms;
            this.change.emit(att);
        }
        this.isLoading.set(false);
    }
}
